<template>
    <div>
        <div class="row">
            <div class="col-12 col-lg-8 col-xl-7 pr-lg-3 pr-0">
                <DirectRecordStage
                    ref="stage"
                    :holistic="holistic"
                    @postprocessed="onPostProcessed"
                    @state-change="onStageStateChange"
                />
                
                <!-- AI Disclaimer - right under video -->
                <div class="mt-2">
                    <div class="ai-disclaimer-text mb-3">
                        {{ $t('sign_feedback.direct_record.ai_disclaimer') }}
                    </div>
                <div class="privacy-info-text" v-html="$t('sign_feedback.direct_record.privacy_info')">
                </div>
                </div>
            </div>

            <div class="col-12 col-lg-4 col-xl-5 w-100 mt-3 mt-lg-0">
                <div class="direct-record-instructions">
                    <h2 class="direct-record-title">{{ $t('sign_feedback.direct_record.title') }}</h2>
                    <p class="direct-record-subtitle">{{ $t('sign_feedback.direct_record.subtitle') }}</p>
                    <div class="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-start btn-box">
                        <Button v-if="firstRecordingDone && !saveLandmarks && !countdown"
                                type="button"
                                class="mb-2 mb-xl-0 mr-xl-3"
                                :disabled="busy || !mediaPipeIsLoaded || isPostProcessing || !postProcessingDone"
                                :loading="busy"
                                label="sign_feedback.direct_record.search_sign"
                                :icon="['fas', 'robot']"
                                @click="sendRecording"></Button>
                        
                        <Button type="button"
                                
                                :disabled="busy || !mediaPipeIsLoaded || !holisticPrewarmed || saveLandmarks || countdown || isCheckingPosition || isPostProcessing || (recordedVideoUrl && !recordedReplayReady)"
                                :loading="!mediaPipeIsLoaded || !holisticPrewarmed"
                                :label="getRecordButtonLabel()"
                                :icon="getRecordButtonIcon()"
                                :variant="getRecordButtonVariant()"
                                @click="handleStartRecording"></Button>
                    </div>

                    <div class="d-none d-xl-flex">
                        <DirectRecordTips />
                    </div>
                </div>
            </div>
        </div>
        
        <div class="d-xl-none mt-3">
            <DirectRecordTips class="instruction-cards--grid" />
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import Button from '@/components/general/Button.vue';
import DirectRecordTips from '@/components/signFeedback/DirectRecordTips.vue';
import DirectRecordStage from '@/components/signFeedback/DirectRecordStage.vue';

@Component({ components: { Button, DirectRecordTips, DirectRecordStage } })
export default class LargeDirectRecordSign extends Vue {

    @Prop() holistic: any;
    @Prop({ type: Boolean, default: false }) skipPositioningCheck!: boolean;
    @Prop({ type: Boolean, default: false }) busy!: boolean;

    @Emit('nextStep')
    public sendRecording() {
        return this.postProcessedLandmarks;
    }

    @Emit('cancel')
    public cancel() {
        return;
    }

    public mediaPipeIsLoaded = false;
    public saveLandmarks = false;
    public firstRecordingDone = false;
    public countdown = false;
    public isCheckingPosition = false;
    public isPostProcessing = false;
    public postProcessingDone = false;
    public recordedVideoUrl: string | null = null;
    public recordedReplayReady = false;
    public holisticPrewarmed = false;
    public postProcessedLandmarks: any[] = [];

    private getRecordButtonLabel(): string {
        if (this.countdown || this.saveLandmarks) {
            return 'sign_feedback.direct_record.recording';
        }
        if (this.isCheckingPosition) {
            return 'sign_feedback.direct_record.positioning.checking';
        }
        if (this.firstRecordingDone) {
            return 'sign_feedback.step2.restart_recording';
        }
        return 'sign_feedback.direct_record.start_recording';
    }

    private getRecordButtonIcon(): string[] {
        if (this.isCheckingPosition) {
            return ['fas', 'search'];
        }
        if (this.firstRecordingDone) {
            return ['fas', 'repeat'];
        }
        return ['fas', 'circle'];
    }

    private getRecordButtonVariant(): 'primary' | 'secondary' {
        if (this.isCheckingPosition) {
            return 'secondary';
        }
        if (this.firstRecordingDone && !this.countdown && !this.saveLandmarks) {
            return 'secondary';
        }
        return 'primary';
    }

    public handleStartRecording() {
        try {
            const activeEl: any = document.activeElement as any;
            if (activeEl && typeof activeEl.blur === 'function') activeEl.blur();
        } catch (e) { /* noop */ }
        const stage: any = this.$refs['stage'];
        if (stage && typeof stage.startRecording === 'function') {
            stage.startRecording();
        }
    }

    public onPostProcessed(frames: any[]) {
        this.postProcessedLandmarks = frames || [];
    }

    public onStageStateChange(payload: any) {
        this.mediaPipeIsLoaded = !!payload.mediaPipeIsLoaded;
        this.holisticPrewarmed = !!payload.holisticPrewarmed;
        this.saveLandmarks = !!payload.saveLandmarks;
        this.firstRecordingDone = !!payload.firstRecordingDone;
        this.countdown = !!payload.countdown;
        this.isCheckingPosition = !!payload.isCheckingPosition;
        this.isPostProcessing = !!payload.isPostProcessing;
        this.postProcessingDone = !!payload.postProcessingDone;
        this.recordedVideoUrl = payload.recordedVideoUrl || null;
        this.recordedReplayReady = !!payload.recordedReplayReady;
    }

    public teardown() {
        const stage: any = this.$refs['stage'];
        if (stage && typeof stage.teardown === 'function') {
            try { stage.teardown(); } catch (e) { /* noop */ }
        }
    }

    public beforeDestroy() {
        const stage: any = this.$refs['stage'];
        if (stage && typeof stage.teardown === 'function') {
            try { stage.teardown(); } catch (e) { /* noop */ }
        }
    }
}
</script>

<style lang="scss" scoped>

.direct-record-title {
    color: #273F7A;
    font-family: 'Raleway', sans-serif;
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.direct-record-subtitle {
    color: #666060;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
}

.btn-box {
    margin-top: 1rem;
}

.btn-box .vgtc-button--secondary:focus,
.btn-box .vgtc-button--secondary:hover {
    background-color: transparent;
    box-shadow: none;
    color: $accent-color-hover;
}

@include media-breakpoint-down(lg) {
    .btn-box .vgtc-button--secondary {
        padding-left: 0;
        margin-left: 0.35rem;
    }
    .direct-record-instructions .btn-box {
        margin-bottom: 0.75rem;
    }
}

@media (min-width: 760px) and (max-width: 992px) {
    .btn-box {
        flex-direction: row !important;
    }
    .btn-box > * {
        margin-bottom: 0 !important;
    }
    .btn-box > *:not(:last-child) {
        margin-right: 0.5rem;
    }
}

.ai-disclaimer-text {
    font-size: 14px;
    color: #6c757d;
    line-height: 1.5;
    margin-bottom: 0.75rem;
}

.privacy-info-text {
    font-size: 13px;
    color: #6c757d;
    line-height: 1.4;
}

.privacy-info-text a {
    color: #007bff;
    text-decoration: underline;
}

.privacy-info-text a:hover {
    color: #0056b3;
    text-decoration: underline;
}

@include media-breakpoint-up(xl) {
    .direct-record-instructions {
        display: flex;
        flex-direction: column;
    }
    .direct-record-instructions .btn-box {
        order: 2;
        margin-top: 1rem;
    }
}
</style>


